import { motion } from "framer-motion";
import { FadeUp } from "@/components/Motion";
import { useI18n } from "@/lib/i18n";
import BeeTileBackdrop from "@/components/BeeTileBackdrop";
import { TestimonialMarquee } from "@/components/TestimonialMarquee";
import { Instagram } from "lucide-react";
import { Seo } from "@/components/Seo";

const FAMIGLIA = {
  de: {
    eyebrow: "UNSERE GEMEINSCHAFT",
    title: ["FAMIGLIA", "MADUBAU"],
    p1: "Als Kunde von Madubau Dolomiti wirst du Teil eines besonderen Kreises von Gleichgesinnten, die echten Luxus, traditionelle Handwerkskunst und alpines Erbe vereinen. Unsere Mitglieder genießen exklusiven Zugang zu maßgeschneiderten Events und persönlichen Erlebnissen, die speziell für sie gestaltet werden.",
    p2: "Vom intimen Honey-Tasting – bei dem du die feinen Nuancen unseres Vanilla Alpine Honey entdeckst – bis hin zu exklusiven Zusammenkünften mit anderen inspirierenden Mitgliedern im Herzen der Dolomiti bietet unsere Gemeinschaft unvergleichliche Vorteile und Momente, die in Erinnerung bleiben. Madubau Dolomiti ist nicht einfach nur PURE ALPINE HONEY, es ist ein Lebensstil.",
    footer:
      "Diese Gemeinschaft ermöglicht dir exklusive Einblicke in unsere Familientradition, außergewöhnliche Vorteile und die Möglichkeit, wertvolle Kontakte zu knüpfen. Werde Teil eines Kreises, der weit über bloßen Genuss hinausgeht — es ist eine Lebenshaltung.",
  },
  en: {
    eyebrow: "OUR COMMUNITY",
    title: ["FAMIGLIA", "MADUBAU"],
    p1: "As a Madubau Dolomiti customer you become part of a select circle of like-minded people who unite true luxury, traditional craftsmanship and alpine heritage. Our members enjoy exclusive access to tailor-made events and personal experiences created just for them.",
    p2: "From an intimate honey tasting — where you discover the subtle nuances of our Vanilla Alpine Honey — to exclusive gatherings with other inspiring members in the heart of the Dolomiti, our community offers unmatched privileges and moments that stay with you. Madubau Dolomiti is not simply PURE ALPINE HONEY, it is a way of living.",
    footer:
      "This community opens exclusive insights into our family tradition, exceptional privileges and the chance to build meaningful connections. Become part of a circle that goes far beyond mere pleasure — it is a philosophy of life.",
  },
  it: {
    eyebrow: "LA NOSTRA COMUNITÀ",
    title: ["FAMIGLIA", "MADUBAU"],
    p1: "Come cliente Madubau Dolomiti entri a far parte di una cerchia speciale di persone affini che uniscono vero lusso, artigianalità tradizionale ed eredità alpina. I nostri membri godono di accesso esclusivo ad eventi su misura ed esperienze personali create appositamente per loro.",
    p2: "Da un'intima degustazione di miele — in cui scopri le sottili sfumature del nostro Vanilla Alpine Honey — a incontri esclusivi con altri membri ispiratori nel cuore delle Dolomiti, la nostra comunità offre privilegi impareggiabili e momenti che restano nella memoria. Madubau Dolomiti non è semplicemente PURE ALPINE HONEY, è uno stile di vita.",
    footer:
      "Questa comunità ti apre le porte a intuizioni esclusive sulla nostra tradizione familiare, vantaggi straordinari e la possibilità di intrecciare relazioni preziose. Entra a far parte di una cerchia che va ben oltre il semplice piacere — è un modo di vivere.",
  },
  ja: {
    eyebrow: "私たちのコミュニティ",
    title: ["FAMIGLIA", "MADUBAU"],
    p1: "Madubau Dolomitiのお客様は、真のラグジュアリー、伝統工芸、アルプスの遺産を分かち合う特別な輪の一員となります。メンバーの皆様には、その方のためにデザインされたオーダーメイドのイベントやパーソナルな体験への特別なアクセスをお楽しみいただけます。",
    p2: "Vanilla Alpine Honeyの繊細なニュアンスを発見する親密なハニーテイスティングから、ドロミテの中心で開かれる刺激的なメンバーとの特別な集いまで——私たちのコミュニティは、比類のない特典と心に残るひとときをお届けします。Madubau Dolomitiは単なるPURE ALPINE HONEYではなく、生き方そのものです。",
    footer:
      "このコミュニティは、私たちの家族の伝統への特別な洞察、格別な特典、そして貴重な人とのつながりを築く機会を開きます。単なる愉しみを超えた輪の一員に——それは人生の哲学です。",
  },
  zh: {
    eyebrow: "我们的社群",
    title: ["FAMIGLIA", "MADUBAU"],
    p1: "成为 Madubau Dolomiti 的客户,您将加入一个特别的圈子——这里汇聚着同样珍视真正奢华、传统工艺与阿尔卑斯传承的人。会员可专享为其量身打造的活动与私人体验。",
    p2: "从私密的蜂蜜品鉴——在其中发现 Vanilla Alpine Honey 的细腻层次——到在多洛米蒂腹地与其他志同道合的会员相聚,我们的社群带来无与伦比的礼遇与难忘时刻。Madubau Dolomiti 不只是 PURE ALPINE HONEY,更是一种生活方式。",
    footer:
      "这个社群让您独家走进我们的家族传统,尊享非凡礼遇,并结识宝贵的人脉。加入一个远超味蕾享受的圈子——这是一种生活态度。",
  },
};

const FAMIGLIA_IMAGE =
  "https://customer-assets-4nw71qhi.emergentagent.net/job_honey-luxury-1/artifacts/o11gaawi_WhatsApp%20Image%202026-07-27%20at%2012.47.09%20%286%29.jpeg";

export default function Gallery() {
  const { t, lang } = useI18n();
  const fam = FAMIGLIA[lang] || FAMIGLIA.de;

  return (
    <div className="pt-32 pb-32 min-h-screen bg-black relative overflow-hidden" data-testid="gallery-page">
      <Seo title={`${t.nav.gallery} — Madubau Dolomiti`} description={t.seo.gallery_d} path="/gallery" />
      <BeeTileBackdrop opacity={0.22} />
      <div className="relative">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <FadeUp className="mb-20 text-center">
          <div className="font-display text-[10px] tracking-[0.4em] text-gold mb-6">/ {t.nav.gallery}</div>
          <h1 className="font-editorial italic font-light text-6xl lg:text-8xl text-cream leading-none mb-8">
            Silence, altitude, gold
          </h1>
          <a
            href="https://www.instagram.com/madubau_honey/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="gallery-ig-link"
            className="inline-flex items-center gap-2 text-gold hover:text-gold-light font-display text-[11px] tracking-[0.35em] link-underline"
          >
            <Instagram className="w-4 h-4" /> @madubau_honey
          </a>
        </FadeUp>

        {/* FAMIGLIA MADUBAU — community section */}
        <section className="mb-28 lg:mb-40" data-testid="famiglia-section">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            {/* LEFT — text */}
            <div className="lg:col-span-6">
              <FadeUp>
                <div className="font-display text-[10px] tracking-[0.45em] text-gold mb-6">
                  / {fam.eyebrow}
                </div>
              </FadeUp>
              <FadeUp>
                <h2
                  className="font-display font-light text-gold leading-[0.95] tracking-tight mb-10"
                  style={{ fontSize: "clamp(2.5rem, 6vw, 5.5rem)" }}
                >
                  {fam.title[0]}
                  <br />
                  {fam.title[1]}
                </h2>
              </FadeUp>
              <FadeUp>
                <p className="text-cream/75 font-body text-base sm:text-lg leading-[1.85] mb-6">
                  {fam.p1}
                </p>
              </FadeUp>
              <FadeUp>
                <p className="text-cream/75 font-body text-base sm:text-lg leading-[1.85]">
                  {fam.p2}
                </p>
              </FadeUp>
            </div>

            {/* RIGHT — dramatic image */}
            <div className="lg:col-span-6">
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                className="relative aspect-[4/5] overflow-hidden bg-[#0d0d0d] border border-gold/20"
                data-testid="famiglia-image"
              >
                <img
                  src={FAMIGLIA_IMAGE}
                  alt="Madubau — Bienen bei der Arbeit"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                {/* subtle warm-gold vignette */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(120% 90% at 50% 50%, rgba(201,162,76,0) 40%, rgba(0,0,0,0.55) 100%)",
                  }}
                />
              </motion.div>
            </div>
          </div>

          {/* Bottom subline */}
          <FadeUp className="mt-16 pt-10 border-t border-gold/15 max-w-4xl mx-auto text-center">
            <p className="text-cream/55 font-editorial italic text-base sm:text-lg leading-relaxed">
              {fam.footer}
            </p>
          </FadeUp>
        </section>

        <TestimonialMarquee />

        <FadeUp className="text-center mt-24">
          <a
            href="https://www.instagram.com/madubau_honey/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 border border-gold/40 text-cream hover:text-black hover:bg-gold transition-colors duration-500 px-10 py-4 font-display text-[11px] tracking-[0.35em]"
            data-testid="gallery-ig-more"
          >
            → MEHR AUF INSTAGRAM
          </a>
        </FadeUp>
      </div>
      </div>
    </div>
  );
}
